import { useContext, useEffect } from 'react';
import { AppContext } from '../../sporttia/AppContext';
import {
    COPILOT_BASE_URL,
    COPILOT_LAUNCHER_ATTRIBUTE,
    isCopilotEnabled,
} from '../../lib/copilot';

declare global {
    interface Window {
        /** API que deja en `window` el `widget.js` de Sporttia Copilot. */
        sporttiaCopilot?: {
            /** Quita el timón y el chat; un script reinyectado lo vuelve a montar. */
            destroy(): void;
        };
    }
}

/**
 * Timón de Sporttia Copilot para los centros que lo tienen. No pinta nada:
 * carga el `widget.js` de Copilot, igual que el Manager nuevo.
 *
 * Es un `<script>` creado a mano porque el script lee sus `data-*` de
 * `document.currentScript`, y al desmontarse hay que poder quitarlo.
 */
export default function CopilotLauncher(): null {
    const cxt = useContext(AppContext);
    const isEnabled = isCopilotEnabled(cxt?.sc);
    const lang = cxt?.lang?.key;
    // No se lee dentro del efecto: está para que cambiar de centro desmonte el
    // timón y lo vuelva a montar con el centro nuevo.
    const sportcenterId = cxt?.sc?.id;

    useEffect(() => {
        if (!isEnabled) return;

        const script = document.createElement('script');
        script.src = `${COPILOT_BASE_URL}/widget.js`;
        script.async = true;
        script.dataset.context = 'manager';
        if (lang) script.dataset.lang = lang;
        document.body.appendChild(script);
        // Activa la variante `copilot:` (src/index.css) con la que lo que vive
        // abajo a la derecha deja sitio al timón.
        document.documentElement.setAttribute(COPILOT_LAUNCHER_ATTRIBUTE, '');

        return () => {
            // Si el script aún no ha cargado, `sporttiaCopilot` no existe todavía.
            window.sporttiaCopilot?.destroy();
            script.remove();
            document.documentElement.removeAttribute(
                COPILOT_LAUNCHER_ATTRIBUTE,
            );
        };
    }, [isEnabled, lang, sportcenterId]);

    return null;
}
